// Import supprimé - les données seront récupérées depuis l'API
// import cvVincentData from '../cvtheques/vincent/cv_vincent.json';

/**
 * Service pour récupérer et gérer les données CV depuis l'API ou en local
 */
export class CVService {
  constructor(locale = 'fr', angle = 'parcours', audience = 'rh') {
    this.locale = locale;
    this.angle = angle;
    this.audience = audience;
    this.cvData = null;
    this.isLoading = false;
    this.error = null;
    // '' = same-origin (single-container : le backend sert aussi le front)
    this.apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? '';
    
    // Données de fallback minimales
    this.cvData = this.getDefaultCVData();
  }

  /**
   * Récupère les données CV depuis l'API
   */
  async fetchCVFromAPI() {
    console.log('🔍 fetchCVFromAPI called');
    console.log('🔧 API Base URL:', this.apiBaseUrl);
    console.log('📋 Current parameters:', { locale: this.locale, angle: this.angle, audience: this.audience });
    
    this.isLoading = true;
    this.error = null;

    try {
      const url = `${this.apiBaseUrl}/cv/vincent?angle=${this.angle}&audience=${this.audience}&locale=${this.locale}`;
      console.log('📡 Fetching CV from:', url);
      
      const response = await fetch(url);
      console.log('📥 Response received:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const apiData = await response.json();
      console.log('📦 Raw API data received:', apiData);
      
      // L'API peut retourner soit CV directement, soit AuthenticCVResponse
      let cvData;
      let backgroundImage = null;
      let profileImage = null;
      
      if (apiData.cv && apiData.profile) {
        // Structure AuthenticCVResponse (authentique + general)
        console.log('📋 Detected AuthenticCVResponse structure');
        cvData = apiData.cv;
        
        // Images dans background et profile
        backgroundImage = apiData.background?.backgroundImage || null;
        profileImage = apiData.profile?.profileImage || null;
        
        console.log('🖼️ Background image from background:', !!backgroundImage);
        console.log('🖼️ Profile image from profile:', !!profileImage);
      } else {
        // Structure CV directe (autres combinaisons via Mistral)
        console.log('📋 Detected direct CV structure');
        cvData = apiData;
        
        // Images dans meta et profile
        backgroundImage = apiData.meta?.backgroundImage || null;
        profileImage = apiData.profile?.profileImage || null;
        
        console.log('🖼️ Background image from meta:', !!backgroundImage);
        console.log('🖼️ Profile image from profile:', !!profileImage);
      }
      
      this.cvData = this.parseCVData(cvData);
      // Stocker les images directement
      this.backgroundImage = backgroundImage;
      this.profileImage = profileImage;
      
      console.log('✅ CV data loaded from API successfully');
      return this.cvData;
      
    } catch (error) {
      console.error('❌ Error fetching CV from API:', error);
      this.error = error.message;
      
      // Fallback vers les données par défaut
      console.log('🔄 Falling back to default data');
      this.cvData = this.getDefaultCVData();
      return this.cvData;
      
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Met à jour les paramètres et recharge les données
   */
  async updateParameters(locale, angle, audience, forceRefresh = false) {
    console.log('🔄 updateParameters called with:', { locale, angle, audience, forceRefresh });
    console.log('📋 Current parameters:', { locale: this.locale, angle: this.angle, audience: this.audience });
    
    const hasChanged = 
      this.locale !== locale || 
      this.angle !== angle || 
      this.audience !== audience;
    
    console.log('🔍 Parameters changed:', hasChanged);
    
    if (hasChanged || forceRefresh) {
      console.log('📝 Updating parameters...');
      this.locale = locale;
      this.angle = angle;
      this.audience = audience;
      
      // Recharger les données avec les nouveaux paramètres
      console.log('🚀 Triggering API call...');
      await this.fetchCVFromAPI();
    } else {
      console.log('⏭️ No parameter change, skipping API call');
    }
  }

  /**
   * Retourne l'état de chargement
   */
  getLoadingState() {
    return {
      isLoading: this.isLoading,
      error: this.error,
      hasData: !!this.cvData
    };
  }

  /**
   * Retourne des données CV par défaut pour le fallback
   */
  getDefaultCVData() {
    return {
      meta: {
        version: "1.0",
        lastUpdated: new Date().toISOString()
      },
      profile: {
        fullName: "Vincent Ostermann",
        title: { fr: "Directeur technique, architecte logiciel & ingénieur système", en: "Technical Director, Software Architect & Systems Engineer" },
        email: "vincent@example.com",
        phone: "+33 6 00 00 00 00",
        location: { fr: "France", en: "France" },
        summary: { fr: "Chargement des données depuis l'API...", en: "Loading data from API..." }
      },
      experiences: [],
      skills: [],
      languages: [],
      hobbies: [],
      education: [],
      projects: []
    };
  }

  /**
   * Parse les données JSON et convertit les dates string en objets Date
   */
  parseCVData(jsonData) {
    const data = { ...jsonData };
    
    // Parser les dates dans les expériences
    if (data.experiences) {
      data.experiences = data.experiences.map(exp => ({
        ...exp,
        dateRange: {
          from: new Date(exp.dateRange.from),
          to: exp.dateRange.to ? new Date(exp.dateRange.to) : null
        }
      }));
    }
    
    // Parser les dates dans l'éducation
    if (data.education) {
      data.education = data.education.map(edu => ({
        ...edu,
        dateRange: {
          from: new Date(edu.dateRange.from),
          to: edu.dateRange.to ? new Date(edu.dateRange.to) : null
        }
      }));
    }
    
    // Parser les dates dans les projets
    if (data.projects) {
      data.projects = data.projects.map(proj => ({
        ...proj,
        dateRange: {
          from: new Date(proj.dateRange.from),
          to: proj.dateRange.to ? new Date(proj.dateRange.to) : null
        }
      }));
    }
    
    return data;
  }

  /**
   * Récupère une valeur traduite selon la locale courante
   */
  getTranslatedValue(translatableString) {
    if (!translatableString) return '';
    if (typeof translatableString === 'string') return translatableString;
    return translatableString[this.locale] || translatableString.fr || translatableString.en || '';
  }

  /**
   * Change la locale
   */
  setLocale(newLocale) {
    this.locale = newLocale;
  }

  /**
   * Récupère le profil complet
   */
  getProfile() {
    return this.cvData.profile;
  }

  /**
   * Récupère toutes les expériences
   */
  getExperiences() {
    return this.cvData.experiences || [];
  }

  /**
   * Récupère une expérience par ID
   */
  getExperienceById(id) {
    return this.cvData.experiences?.find(exp => exp.id === id);
  }

  /**
   * Récupère la formation
   */
  getEducation() {
    return this.cvData.education || [];
  }

  /**
   * Récupère les projets
   */
  getProjects() {
    return this.cvData.projects || [];
  }

  /**
   * Récupère les compétences
   */
  getSkills() {
    return this.cvData.skills || [];
  }

  /**
   * Récupère les compétences par type
   */
  getSkillsByType(type) {
    return this.cvData.skills?.filter(skill => skill.type === type) || [];
  }

  /**
   * Récupère les langues
   */
  getLanguages() {
    return this.cvData.languages || [];
  }

  /**
   * Récupère les centres d'intérêt
   */
  getHobbies() {
    return this.cvData.hobbies || [];
  }

  /**
   * Récupère les métadonnées
   */
  getMetadata() {
    return this.cvData.meta;
  }

  /**
   * Récupère toutes les questions/réponses d'une section
   */
  getSectionQA(sectionId) {
    const experience = this.getExperienceById(sectionId);
    if (!experience) return [];

    const allQA = [];
    
    if (experience.data) {
      experience.data.forEach(achievement => {
        if (achievement.qa) {
          achievement.qa.forEach(qa => {
            allQA.push({
              question: this.getTranslatedValue(qa.question),
              answer: this.getTranslatedValue(qa.answer),
              achievementId: achievement.id
            });
          });
        }
      });
    }

    return allQA;
  }

  /**
   * Recherche une réponse basée sur une question
   */
  findAnswerForQuestion(sectionId, userQuestion) {
    const qa = this.getSectionQA(sectionId);
    
    // Recherche exacte
    const exactMatch = qa.find(item => 
      item.question.toLowerCase() === userQuestion.toLowerCase()
    );
    if (exactMatch) return exactMatch.answer;

    // Recherche par mots-clés
    const userKeywords = userQuestion.toLowerCase().split(' ').filter(word => word.length > 2);
    
    for (const item of qa) {
      const questionKeywords = item.question.toLowerCase().split(' ');
      const matches = userKeywords.filter(keyword => 
        questionKeywords.some(qKeyword => 
          qKeyword.includes(keyword) || keyword.includes(qKeyword)
        )
      );
      
      if (matches.length >= 2) {
        return item.answer;
      }
    }

    // Réponse par défaut basée sur les données de l'expérience
    const experience = this.getExperienceById(sectionId);
    if (experience) {
      const missionSummary = this.getTranslatedValue(experience.missionSummary);
      if (missionSummary) {
        return missionSummary;
      }
    }

    return this.locale === 'fr' ? 
      "Je n'ai pas d'information spécifique sur cette question. Pouvez-vous reformuler ou être plus précis ?" :
      "I don't have specific information about this question. Could you rephrase or be more specific?";
  }

  /**
   * Formate une date selon la locale
   */
  formatDate(date) {
    if (!date) return this.locale === 'fr' ? 'Présent' : 'Present';
    
    // Si c'est déjà une string d'année (4 chiffres)
    if (typeof date === 'string' && /^\d{4}$/.test(date)) {
      return date;
    }
    
    // Si c'est une string de date ISO (format 2023-01-01T00:00:00.000Z)
    if (typeof date === 'string' && date.includes('T')) {
      try {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.getFullYear().toString();
        }
      } catch (error) {
        console.warn('⚠️ Unable to parse ISO date:', date);
      }
    }
    
    // Si c'est une string de date simple (format YYYY-MM-DD)
    if (typeof date === 'string' && date.includes('-')) {
      const year = date.split('-')[0];
      if (/^\d{4}$/.test(year)) {
        return year;
      }
    }
    
    // Si c'est un nombre (année)
    if (typeof date === 'number') {
      return date.toString();
    }
    
    // Si c'est un objet Date valide
    if (date instanceof Date && !isNaN(date.getTime())) {
      return date.getFullYear().toString();
    }
    
    // Fallback : essayer de parser comme Date
    try {
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.getFullYear().toString();
      }
    } catch (error) {
      console.warn('⚠️ Unable to parse date:', date);
    }
    
    // Si tout échoue, retourner la valeur telle quelle ou un fallback
    return date?.toString() || (this.locale === 'fr' ? 'Présent' : 'Present');
  }

  /**
   * Formate une période de dates
   */
  formatDateRange(dateRange) {
    const from = this.formatDate(dateRange.from);
    const to = this.formatDate(dateRange.to);
    return `${from} - ${to}`;
  }

  /**
   * Récupère l'image de profil en base64 depuis le backend
   */
  getProfileImage() {
    if (this.profileImage) {
      console.log('🖼️ Using profile image from API');
      // L'image contient déjà le préfixe data:image/jpeg;base64, si nécessaire
      return this.profileImage.startsWith('data:') ? this.profileImage : `data:image/jpeg;base64,${this.profileImage}`;
    }
    console.log('🖼️ No profile image available from API');
    return null;
  }

  /**
   * Récupère l'image de background en base64 depuis le backend
   */
  getBackgroundImage() {
    if (this.backgroundImage) {
      console.log('🖼️ Using background image from API');
      // L'image contient déjà le préfixe data:image/jpeg;base64, si nécessaire
      return this.backgroundImage.startsWith('data:') ? this.backgroundImage : `data:image/jpeg;base64,${this.backgroundImage}`;
    }
    console.log('🖼️ No background image available from API');
    return null;
  }

  /**
   * Vérifie si des images sont disponibles depuis l'API
   */
  hasImages() {
    return !!(this.profileImage || this.backgroundImage);
  }

  /**
   * Récupère les données d'images disponibles
   */
  getImagesData() {
    return {
      profileImage: this.profileImage,
      backgroundImage: this.backgroundImage,
      hasProfileImage: !!this.profileImage,
      hasBackgroundImage: !!this.backgroundImage
    };
  }
}

// Instance par défaut
export const cvService = new CVService();
